import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import MuiAvatar from '@mui/material/Avatar';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import Button from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import Avatar, { stringToHslColor } from '@components/Avatar';
import { Chip } from '@components/Chip';
import { ModalConfirm } from '@components/Modal';
import { MenuTinyArrow } from '@components/MenuTinyArrow';
import { Typography } from '@components/Typography';
import UsersMenu from '@features/UsersMenu';
import usePermissions from '@hooks/usePermissions';
import { LabelPlain } from '@components/Label';
import { TagChip } from '@components/TagChip';
import TagEditModal from '@components/TagEditModal';
import { InfoWrapper } from '@features/ContactInfo/styles';
import { formatDateOrEmpty } from '@helpers/dates';
import { getConversationChannelIcon } from '@helpers/channels';
import { getFullName, getInitialsFull } from '@helpers/userName';
import { useAccountPeople, useTranslation, userCoversTeam, userHasInboxAccess } from '@hooks';
import { ConversationStatus } from '@constants/conversations';
import { TAG_NAME_MAX_LENGTH } from '@constants/conversation-tags';
import { useMoveToTeam } from '../useMoveToTeam';
import { useAssignConversation } from '../useAssignConversation';
import { useAssigneeOptions } from '../useAssigneeOptions';
import { useCollaborators } from '../useCollaborators';
import { useConversationTags } from '../useConversationTags';

const Section = styled('div')({
    marginBottom: 24,
});

const IconRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    '& > svg': {
        fontSize: 16,
        color: 'var(--text-tertiary)',
        flex: 'none',
    },
});

const ChannelIconSlot = styled('span')({
    display: 'flex',
    flex: 'none',
    '& svg': {
        width: 16,
        height: 16,
    },
});

// Same trigger shape as the reply-templates menu button in the review response box
const teamButtonSx = {
    fontSize: '0.875rem',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    height: 24,
    padding: '4px 8px',
    marginLeft: '-8px',
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'flex-start',
};

const CollaboratorsRow = ({ activeConversation, setActiveConversation, filterRef, canManage }) => {
    const [addAnchorEl, setAddAnchorEl] = useState(null);

    const users = useSelector(state => state.account?.account?.users || []);
    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const { addCollaborator, removeCollaborator } = useCollaborators({ setActiveConversation, filterRef });

    const collaboratorIds = activeConversation.collaborators || [];
    const collaboratorUsers = collaboratorIds.map(id => users.find(u => u.id === id)).filter(Boolean);
    const candidates = users.filter(u => !collaboratorIds.includes(u.id) && userHasInboxAccess(u));

    return (
        <InfoWrapper>
            <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                {t('InboxPage.collaborators')}
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                {collaboratorUsers.map(user => {
                    const fullName = getFullName(user.firstName, user.lastName);
                    return (
                        <Chip
                            key={user.id}
                            avatar={
                                <MuiAvatar
                                    src={user.profileImageUrl}
                                    sx={{
                                        backgroundColor: stringToHslColor(fullName, 30, 85),
                                        color: `${stringToHslColor(fullName, 30, 25)} !important`,
                                    }}
                                >
                                    {getInitialsFull(fullName)}
                                </MuiAvatar>
                            }
                            label={fullName}
                            onDelete={
                                canManage || user.id === currentUser?.id
                                    ? () => removeCollaborator(activeConversation.id, user.id)
                                    : undefined
                            }
                        />
                    );
                })}
                {canManage && (
                    <Button
                        type="button"
                        variant="text"
                        sx={{ height: 24, minWidth: 0, padding: '4px 8px' }}
                        onClick={event => setAddAnchorEl(event.currentTarget)}
                        disabled={!candidates.length}
                    >
                        <AddRoundedIcon sx={{ fontSize: 16 }} />
                        {t('InboxPage.addCollaborator')}
                    </Button>
                )}
            </div>
            <Menu
                anchorEl={addAnchorEl}
                open={!!addAnchorEl}
                onClose={() => setAddAnchorEl(null)}
                sx={{ '.MuiMenu-paper': { borderRadius: '8px', maxHeight: 320 } }}
            >
                {candidates.map(user => {
                    const fullName = getFullName(user.firstName, user.lastName);
                    return (
                        <MenuItem
                            key={user.id}
                            onClick={() => {
                                setAddAnchorEl(null);
                                addCollaborator(activeConversation.id, user.id);
                            }}
                        >
                            <Avatar
                                size="small"
                                fullName={fullName}
                                label={getInitialsFull(fullName)}
                                image={user.profileImageUrl}
                            />
                            <Typography variant="body2" sx={{ marginLeft: '8px' }}>
                                {fullName}
                            </Typography>
                        </MenuItem>
                    );
                })}
            </Menu>
        </InfoWrapper>
    );
};

const TagsRow = ({ activeConversation, setActiveConversation, filterRef, canManage }) => {
    const [addAnchorEl, setAddAnchorEl] = useState(null);
    const [newTagName, setNewTagName] = useState('');
    const [tagToEdit, setTagToEdit] = useState(null);
    const [tagToDelete, setTagToDelete] = useState(null);

    const { data: tagCatalog } = useSelector(state => state.conversationTags);

    const { t } = useTranslation();
    const { hasFullAccess } = usePermissions();
    const { addTag, removeTag, createAndAssignTag, updateTag, deleteTag } = useConversationTags({
        setActiveConversation,
        filterRef,
    });

    const canManageCatalog = hasFullAccess('messaging');

    const rawTags = activeConversation.tags || [];
    const assignedTags = rawTags.map(tag => tagCatalog.find(t => t.id === tag.id) || tag);
    const assignedIds = assignedTags.map(tag => tag.id);

    const closeMenu = () => {
        setAddAnchorEl(null);
        setNewTagName('');
    };

    const onToggle = tagId => {
        closeMenu();
        if (assignedIds.includes(tagId)) {
            removeTag(activeConversation.id, tagId);
        } else {
            addTag(activeConversation.id, tagId);
        }
    };

    const onCreate = () => {
        const name = newTagName.trim();
        if (!name) {
            return;
        }
        closeMenu();
        createAndAssignTag(activeConversation.id, name);
    };

    return (
        <InfoWrapper>
            <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                {t('InboxPage.tags')}
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                {assignedTags.map(tag => (
                    <TagChip
                        key={tag.id}
                        name={tag.name}
                        color={tag.color}
                        onDelete={canManage ? () => removeTag(activeConversation.id, tag.id) : undefined}
                    />
                ))}
                {canManage && (
                    <Button
                        type="button"
                        variant="text"
                        sx={{ height: 24, minWidth: 0, padding: '4px 8px' }}
                        onClick={event => setAddAnchorEl(event.currentTarget)}
                        disabled={!canManageCatalog && !tagCatalog.length}
                    >
                        <AddRoundedIcon sx={{ fontSize: 16 }} />
                        {t('InboxPage.addTag')}
                    </Button>
                )}
            </div>
            <Menu
                anchorEl={addAnchorEl}
                open={!!addAnchorEl}
                onClose={closeMenu}
                autoFocus={!canManageCatalog}
                sx={{ '.MuiMenu-paper': { borderRadius: '8px', maxHeight: 320, minWidth: 220 } }}
            >
                {canManageCatalog && (
                    <div style={{ padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <InputBase
                            autoFocus
                            placeholder={t('InboxPage.tagNamePlaceholder')}
                            value={newTagName}
                            onChange={event => setNewTagName(event.target.value)}
                            onKeyDown={event => {
                                event.stopPropagation();
                                if (event.key === 'Enter') {
                                    onCreate();
                                }
                            }}
                            sx={{ fontSize: 14, flex: 1 }}
                        />
                        <Button
                            type="button"
                            variant="text"
                            sx={{ height: 24, minWidth: 0, padding: '4px 8px', whiteSpace: 'nowrap' }}
                            disabled={!newTagName.trim()}
                            onClick={onCreate}
                        >
                            {t('InboxPage.createNewTag')}
                        </Button>
                    </div>
                )}
                {tagCatalog.map(tag => (
                    <MenuItem key={tag.id} onClick={() => onToggle(tag.id)} sx={{ gap: '8px' }}>
                        <Checkbox
                            checked={assignedIds.includes(tag.id)}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '1.125rem' } }}
                        />
                        <LabelRoundedIcon sx={{ fontSize: 18, color: tag.color }} />
                        <Typography
                            variant="body2"
                            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {tag.name}
                        </Typography>
                        {canManageCatalog && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        closeMenu();
                                        setTagToEdit(tag);
                                    }}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        closeMenu();
                                        setTagToDelete(tag);
                                    }}
                                >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </>
                        )}
                    </MenuItem>
                ))}
            </Menu>
            {!!tagToEdit && (
                <TagEditModal
                    tag={tagToEdit}
                    title={t('InboxPage.editTagTitle')}
                    colorLabel={t('InboxPage.tagColor')}
                    maxLength={TAG_NAME_MAX_LENGTH}
                    onClose={() => setTagToEdit(null)}
                    onSave={async tag => {
                        if (await updateTag(tag)) {
                            setTagToEdit(null);
                        }
                    }}
                />
            )}
            {!!tagToDelete && (
                <ModalConfirm
                    isOpen={!!tagToDelete}
                    handleClose={() => setTagToDelete(null)}
                    title={t('InboxPage.deleteTagTitle')}
                    description={t('InboxPage.deleteTagDescription', { name: tagToDelete.name })}
                    onPrimaryAction={() => {
                        deleteTag(tagToDelete.id);
                        setTagToDelete(null);
                    }}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </InfoWrapper>
    );
};

const ConversationDetails = ({ activeConversation, setActiveConversation, filterRef }) => {
    const currentUser = useSelector(state => state.auth.user);
    const language = currentUser?.language;

    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const getAssigneeOptions = useAssigneeOptions();
    const { canMoveToTeam, openMoveToTeam, moveToTeamModal } = useMoveToTeam({
        activeConversation,
        setActiveConversation,
        filterRef,
    });
    const assignConversation = useAssignConversation({ setActiveConversation, filterRef });

    const usersOptions = useMemo(
        () => getAssigneeOptions(activeConversation?.teamId),
        [activeConversation?.teamId, getAssigneeOptions],
    );

    const assignedOption = usersOptions.find(option => option.value === (activeConversation?.assignedUserId ?? null));
    const assigneeLabel =
        assignedOption?.label || getPersonName(activeConversation?.assignedUserId) || t('common.unassigned');

    const onAssignUser = userId => {
        if (activeConversation?.assignedUserId === userId) {
            return;
        }
        return assignConversation(activeConversation?.id, userId);
    };

    if (!activeConversation) {
        return null;
    }

    const isOpen = activeConversation.status === ConversationStatus.Open;
    // Mirror the backend guard: assignment and collaborator management are team-level; cross-team collaborators read/note only.
    const canManage = userCoversTeam(currentUser, activeConversation.teamId);
    const formatDate = date => formatDateOrEmpty(date, 'd MMM yyyy • HH:mm', language);

    return (
        <Section>
            <InfoWrapper>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                    {t('common.status')}
                </Typography>
                <LabelPlain
                    variant="caption"
                    size="small"
                    type={isOpen ? 'Promoter' : undefined}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {t(isOpen ? 'InboxPage.open' : 'InboxPage.resolved')}
                </LabelPlain>
            </InfoWrapper>
            <InfoWrapper>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                    {t('common.team')}
                </Typography>
                {canMoveToTeam ? (
                    <Button type="button" variant="text" sx={teamButtonSx} onClick={openMoveToTeam}>
                        <GroupsOutlinedIcon sx={{ fontSize: 16, marginRight: '6px', flex: 'none' }} />
                        <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {activeConversation.teamName || '--'}
                        </span>
                        <MenuTinyArrow />
                    </Button>
                ) : (
                    <IconRow>
                        <GroupsOutlinedIcon />
                        <Typography
                            variant="body2"
                            sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                        >
                            {activeConversation.teamName || '--'}
                        </Typography>
                    </IconRow>
                )}
            </InfoWrapper>
            <InfoWrapper>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                    {t('common.assignedTo')}
                </Typography>
                {activeConversation.assignedAgentId ? (
                    <Typography variant="body2">{t('InboxPage.pulseAgent')}</Typography>
                ) : canManage ? (
                    <UsersMenu
                        options={usersOptions}
                        value={activeConversation.assignedUserId ?? null}
                        onSelect={onAssignUser}
                        label={assigneeLabel}
                    />
                ) : (
                    <Typography variant="body2">{assigneeLabel}</Typography>
                )}
            </InfoWrapper>
            <CollaboratorsRow
                activeConversation={activeConversation}
                setActiveConversation={setActiveConversation}
                filterRef={filterRef}
                canManage={canManage}
            />
            <TagsRow
                activeConversation={activeConversation}
                setActiveConversation={setActiveConversation}
                filterRef={filterRef}
                canManage={canManage}
            />
            <InfoWrapper>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                    {t('common.channel')}
                </Typography>
                <IconRow>
                    <ChannelIconSlot>{getConversationChannelIcon(activeConversation)}</ChannelIconSlot>
                    <Typography
                        variant="body2"
                        sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                    >
                        {activeConversation.channelName || t('common.archived')}
                    </Typography>
                </IconRow>
            </InfoWrapper>
            <InfoWrapper>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                    {t('InboxPage.createdAt')}
                </Typography>
                <Typography variant="body2">{formatDate(activeConversation.createdAt)}</Typography>
            </InfoWrapper>
            {activeConversation.closedAt && (
                <InfoWrapper>
                    <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                        {t('InboxPage.resolvedAt')}
                    </Typography>
                    <Typography variant="body2">{formatDate(activeConversation.closedAt)}</Typography>
                </InfoWrapper>
            )}
            {moveToTeamModal}
        </Section>
    );
};

export default ConversationDetails;
